import React, { SyntheticEvent, useMemo } from 'react';
import {
  PMCConfig,
  CustomCtaOption,
  ExtendedProvider,
  TrackFn,
  PatientRelationship
} from '@kyruus/types';
import {
  DefaultCta,
  BookOnlineCta,
  VirtualCareCta,
  PhoneCta,
  RequestFormCta,
  CustomCta,
  getRenderableCtas,
  getCtaConfig
} from '@kyruus/provider-components';
import { List } from '../styles';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { WrappedComponentProps } from '@kyruus/intl';
import { getConfigProperty } from 'Common/config';

interface BaseCtaListProps {
  onClose: (
    e: SyntheticEvent,
    reason?: 'backdropClick' | 'escapeKeyDown' | 'closeButton'
  ) => void;
  provider: ExtendedProvider;
  config: PMCConfig;
  bookOnlineUrl: string | null;
  log: TrackFn;
  patientRelationship?: PatientRelationship;
}

type CtaListProps = BaseCtaListProps &
  WrappedComponentProps &
  RouteComponentProps;

const BaseCtaList = ({
  provider,
  config,
  bookOnlineUrl,
  log,
  patientRelationship,
  location,
  history
}: CtaListProps) => {
  const isAvailabilityEnabled = getConfigProperty(
    config,
    'display_availability_in_search'
  );
  const ctaListItems = useMemo(
    () =>
      getRenderableCtas({
        provider,
        ctaConfig: getCtaConfig(config, patientRelationship),
        patientRelationship,
        isAvailabilityEnabled
      }),
    [config, provider, patientRelationship, isAvailabilityEnabled]
  );
  function renderListItem(listItem: DefaultCta | CustomCtaOption) {
    const isDefaultListItem = typeof listItem === 'string';
    if (!isDefaultListItem) {
      return (
        <CustomCta
          component="li"
          key={(listItem as CustomCtaOption).id}
          provider={provider}
          customOption={listItem as CustomCtaOption}
          track={log}
          config={config}
        />
      );
    }
    switch (listItem) {
      case DefaultCta.BookOnline:
        if (!bookOnlineUrl) {
          return null;
        }
        return (
          <BookOnlineCta
            component="li"
            key={listItem}
            provider={provider}
            config={config}
            bookOnlineUrl={bookOnlineUrl}
            track={log}
            onScheduleDrawerClick={() =>
              history.push(bookOnlineUrl, { provider: { location, provider } })
            }
          />
        );
      case DefaultCta.Phone:
        return (
          <PhoneCta
            component="li"
            key={listItem}
            provider={provider}
            track={log}
          />
        );
      case DefaultCta.RequestForm:
        return (
          <RequestFormCta
            component="li"
            key={listItem}
            provider={provider}
            track={log}
          />
        );
      case DefaultCta.VirtualCare:
        return (
          <VirtualCareCta
            component="li"
            key={listItem}
            provider={provider}
            track={log}
          />
        );
      default:
        return null;
    }
  }
  return (
    <List disablePadding={true}>
      {ctaListItems.map((item) => {
        return renderListItem(item);
      })}
    </List>
  );
};

export const CtaList = withRouter(BaseCtaList) as any as (
  props: BaseCtaListProps
) => React.ReactElement;
